/**
 * useActorConstellations — Find all constellations an actor participates in.
 *
 * DRR-126 Phase 4: Role-scoped constellation query. Given an actor ID, returns
 * all constellation IDs where the actor appears as Ring 1 (coordinator), Ring 2
 * (authority), or Ring 4 (reviewer) — derived from Valkey reverse edge indexes.
 *
 * This enables "show me all constellations for this actor" — the actor-centric
 * view that powers ring 1 peer awareness and scope-filtered constellation lists.
 *
 * Data is T2 (operational). Backend enforces visibility per DRR-102.
 * No frontend role logic. Backend filters by actor's scope.
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const CONSTELLATIONS_BY_ACTOR_QUERY = gql`
  query ConstellationsByActor($actorId: String) {
    constellationsByActor(actorId: $actorId) {
      actorId
      constellationIds
      memberships {
        constellationId
        ring
        edgeType
        role
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ActorConstellationMembership {
  constellationId: string;
  /** Ring number: 1=coordinator, 2=authority, 4=reviewer */
  ring: number;
  /** Inverse edge type: COORDINATES | AUTHORIZES | REVIEWS */
  edgeType: string;
  role: string;
}

interface QueryData {
  constellationsByActor: {
    actorId: string;
    constellationIds: string[];
    memberships: ActorConstellationMembership[];
  };
}

export interface ActorConstellationsResult {
  actorId: string;
  constellationIds: string[];
  memberships: ActorConstellationMembership[];
  /** Memberships keyed by constellationId for O(1) lookup */
  byConstellation: Map<string, ActorConstellationMembership>;
  loading: boolean;
  error?: Error;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetch all constellations an actor participates in via Valkey reverse edges.
 *
 * @param actorId - Sequential actor ID (e.g., "42_3"). If undefined, backend
 *                  uses the authenticated actor's identity.
 * @param skip - Skip the query (e.g., when actor context is not yet available)
 */
export function useActorConstellations(
  actorId?: string,
  skip = false,
): ActorConstellationsResult {
  const { data, loading, error } = useQuery<QueryData>(
    CONSTELLATIONS_BY_ACTOR_QUERY,
    {
      variables: { actorId: actorId ?? null },
      skip,
      fetchPolicy: 'cache-and-network',
    },
  );

  const memberships = data?.constellationsByActor?.memberships ?? [];

  const byConstellation = useMemo<Map<string, ActorConstellationMembership>>(() => {
    const map = new Map<string, ActorConstellationMembership>();
    for (const m of memberships) {
      map.set(m.constellationId, m);
    }
    return map;
  }, [memberships]);

  return {
    actorId: data?.constellationsByActor?.actorId ?? actorId ?? '',
    constellationIds: data?.constellationsByActor?.constellationIds ?? [],
    memberships,
    byConstellation,
    loading,
    error: error as Error | undefined,
  };
}
